import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  ExchangeCartLine,
  ExchangeCartPair,
  ExchangeIssuedItem,
  ExchangeReplacementItem,
  ExchangeReturnDisposition,
} from './types';

export interface UseExchangeStateParams {
  issuedItems: ExchangeIssuedItem[];
  replacementItems: ExchangeReplacementItem[];
}

export interface UseExchangeStateResult {
  focusedIssuedItemId: string | null;
  pairedReplacementByIssuedId: Map<string, string>;
  cartPairs: ExchangeCartPair[];
  cartLines: ExchangeCartLine[];
  replacementQtyByIssuedId: Record<string, number>;
  signatureAcknowledged: boolean;
  canComplete: boolean;
  focusIssuedItem: (issuedItemId: string | null) => void;
  setReplacementQty: (issuedItemId: string, qty: number) => void;
  stageReplacement: (
    issuedItemId: string,
    replacementItemId: string,
    disposition: ExchangeReturnDisposition,
  ) => void;
  removePair: (issuedItemId: string) => void;
  setSignatureAcknowledged: (acknowledged: boolean) => void;
  reset: () => void;
}

const createPairedMap = (pairs: ExchangeCartPair[]) => {
  return pairs.reduce<Map<string, string>>((accumulator, pair) => {
    accumulator.set(pair.issuedItemId, pair.replacementItemId);
    return accumulator;
  }, new Map());
};

export const useExchangeState = ({
  issuedItems,
  replacementItems,
}: UseExchangeStateParams): UseExchangeStateResult => {
  const [focusedIssuedItemId, setFocusedIssuedItemId] = useState<string | null>(
    issuedItems[0]?.id ?? null,
  );
  const [cartPairs, setCartPairs] = useState<ExchangeCartPair[]>([]);
  const [replacementQtyByIssuedId, setReplacementQtyByIssuedId] = useState<Record<string, number>>(
    {},
  );
  const [signatureAcknowledged, setSignatureAcknowledgedState] = useState(false);

  useEffect(() => {
    setReplacementQtyByIssuedId((previous) => {
      if (Object.keys(previous).length === 0) {
        return previous;
      }

      const next: Record<string, number> = {};
      let hasChanges = false;

      issuedItems.forEach((item) => {
        const existing = previous[item.id];
        if (typeof existing !== 'number') {
          return;
        }
        const clamped = Math.max(0, Math.min(item.issuedQty, existing));
        next[item.id] = clamped;
        if (clamped !== existing) {
          hasChanges = true;
        }
      });

      if (Object.keys(next).length !== Object.keys(previous).length) {
        hasChanges = true;
      }

      return hasChanges ? next : previous;
    });
  }, [issuedItems]);

  useEffect(() => {
    if (cartPairs.length === 0) {
      return;
    }

    const issuedById = new Map(issuedItems.map((item) => [item.id, item] as const));
    const replacementById = new Map(replacementItems.map((item) => [item.id, item] as const));
    const nextPairs = cartPairs.filter((pair) => {
      const issued = issuedById.get(pair.issuedItemId);
      const replacement = replacementById.get(pair.replacementItemId);
      if (!issued || !replacement) {
        return false;
      }

      const issuedParent = issued.parentCatalogId;
      const replacementParent = replacement.parentCatalogId;

      if (issuedParent && replacementParent) {
        if (issuedParent !== replacementParent) {
          return false;
        }
      } else {
        if (issued.lin !== replacement.lin) {
          return false;
        }
      }

      return replacement.assetIds.includes(pair.replacementAssetId);
    });

    if (nextPairs.length !== cartPairs.length) {
      setCartPairs(nextPairs);
      setSignatureAcknowledgedState(false);
    }
  }, [issuedItems, replacementItems, cartPairs]);

  useEffect(() => {
    if (focusedIssuedItemId && issuedItems.some((item) => item.id === focusedIssuedItemId)) {
      return;
    }

    const fallbackId = issuedItems[0]?.id ?? null;
    setFocusedIssuedItemId(fallbackId);
  }, [focusedIssuedItemId, issuedItems]);

  const pairedReplacementByIssuedId = useMemo(() => createPairedMap(cartPairs), [cartPairs]);

  const cartLines = useMemo(() => {
    if (cartPairs.length === 0) {
      return [];
    }

    const issuedById = new Map(issuedItems.map((item) => [item.id, item] as const));
    const replacementById = new Map(replacementItems.map((item) => [item.id, item] as const));

    return cartPairs
      .map((pair) => {
        const issuedItem = issuedById.get(pair.issuedItemId);
        const replacementItem = replacementById.get(pair.replacementItemId);

        if (!issuedItem || !replacementItem) {
          return null;
        }

        return {
          issuedItem,
          replacementItem,
          replacementAssetId: pair.replacementAssetId,
          returnDisposition: pair.returnDisposition,
        } satisfies ExchangeCartLine;
      })
      .filter((line): line is ExchangeCartLine => line !== null);
  }, [cartPairs, issuedItems, replacementItems]);

  const allLinesHaveDisposition = cartLines.every((line) => Boolean(line.returnDisposition));
  const canComplete = cartLines.length > 0 && signatureAcknowledged && allLinesHaveDisposition;

  const focusIssuedItem = useCallback(
    (issuedItemId: string | null) => {
      if (!issuedItemId) {
        setFocusedIssuedItemId(null);
        return;
      }

      if (!issuedItems.some((item) => item.id === issuedItemId)) {
        return;
      }

      setFocusedIssuedItemId(issuedItemId);
    },
    [issuedItems],
  );

  const setSignatureAcknowledged = useCallback((acknowledged: boolean) => {
    setSignatureAcknowledgedState(acknowledged);
  }, []);

  const setReplacementQty = useCallback(
    (issuedItemId: string, qty: number) => {
      const issuedItem = issuedItems.find((item) => item.id === issuedItemId);
      if (!issuedItem) {
        return;
      }

      const maxQty = Math.max(0, issuedItem.issuedQty);
      const normalized = Number.isFinite(qty) ? qty : 0;
      const clamped = Math.max(0, Math.min(maxQty, normalized));

      setReplacementQtyByIssuedId((previous) => {
        if (previous[issuedItemId] === clamped) {
          return previous;
        }
        return { ...previous, [issuedItemId]: clamped };
      });
    },
    [issuedItems],
  );

  const stageReplacement = useCallback(
    (issuedItemId: string, replacementItemId: string, disposition: ExchangeReturnDisposition) => {
      const issuedItem = issuedItems.find((item) => item.id === issuedItemId);
      const replacementItem = replacementItems.find((item) => item.id === replacementItemId);

      if (!issuedItem || !replacementItem) {
        return;
      }

      const issuedParent = issuedItem.parentCatalogId;
      const replacementParent = replacementItem.parentCatalogId;

      if (issuedParent && replacementParent) {
        if (issuedParent !== replacementParent) {
          return;
        }
      } else {
        if (issuedItem.lin !== replacementItem.lin) {
          return;
        }
      }

      setCartPairs((previous) => {
        const existingPair = previous.find((pair) => pair.issuedItemId === issuedItemId) ?? null;
        const nextPairs = previous.filter((pair) => pair.issuedItemId !== issuedItemId);
        const reservedAssetIds = new Set(nextPairs.map((pair) => pair.replacementAssetId));

        const availableAssetId =
          replacementItem.assetIds.find((assetId) => !reservedAssetIds.has(assetId)) ??
          existingPair?.replacementAssetId ??
          null;

        if (!availableAssetId) {
          return previous;
        }

        nextPairs.push({
          issuedItemId,
          replacementItemId,
          replacementAssetId: availableAssetId,
          returnDisposition: disposition,
        });
        return nextPairs;
      });
      setSignatureAcknowledgedState(false);
      setFocusedIssuedItemId(issuedItemId);
    },
    [issuedItems, replacementItems],
  );

  const removePair = useCallback((issuedItemId: string) => {
    setCartPairs((previous) => {
      const nextPairs = previous.filter((pair) => pair.issuedItemId !== issuedItemId);
      if (nextPairs.length === previous.length) {
        return previous;
      }
      return nextPairs;
    });
    setSignatureAcknowledgedState(false);
  }, []);

  const reset = useCallback(() => {
    setCartPairs([]);
    setSignatureAcknowledgedState(false);
    setReplacementQtyByIssuedId({});
    setFocusedIssuedItemId(issuedItems[0]?.id ?? null);
  }, [issuedItems]);

  return {
    focusedIssuedItemId,
    pairedReplacementByIssuedId,
    cartPairs,
    cartLines,
    replacementQtyByIssuedId,
    signatureAcknowledged,
    canComplete,
    focusIssuedItem,
    setReplacementQty,
    stageReplacement,
    removePair,
    setSignatureAcknowledged,
    reset,
  };
};
